import { useCallback, useEffect, useRef, useState } from "react"
import type { Photo } from "../types"

interface PhotoGalleryProps {
  photos: Photo[]
  startIndex: number
  onClose: () => void
}

export default function PhotoGallery({ photos, startIndex, onClose }: PhotoGalleryProps) {
  const [index, setIndex] = useState(startIndex)
  const [scale, setScale] = useState(1)
  const containerRef = useRef<HTMLDivElement>(null)
  const touchStartX = useRef(0)
  const touchStartY = useRef(0)

  const goNext = useCallback(() => {
    if (index < photos.length - 1) {
      setIndex((i) => i + 1)
      setScale(1)
    }
  }, [index, photos.length])

  const goPrev = useCallback(() => {
    if (index > 0) {
      setIndex((i) => i - 1)
      setScale(1)
    }
  }, [index])

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
      if (e.key === "ArrowRight") goNext()
      if (e.key === "ArrowLeft") goPrev()
    }
    window.addEventListener("keydown", handleKey)
    return () => window.removeEventListener("keydown", handleKey)
  }, [onClose, goNext, goPrev])

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
    touchStartY.current = e.touches[0].clientY
  }

  const handleTouchEnd = (e: React.TouchEvent) => {
    const dx = e.changedTouches[0].clientX - touchStartX.current
    const dy = e.changedTouches[0].clientY - touchStartY.current
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) {
      if (dx < 0) goNext()
      else goPrev()
    }
  }

  const photo = photos[index]
  if (!photo) return null

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-50 bg-black flex flex-col"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="flex items-center justify-between px-4 py-3">
        <span className="text-white text-sm">
          {index + 1} / {photos.length}
        </span>
        <button
          onClick={onClose}
          className="text-white text-3xl leading-none hover:text-gray-300"
        >
          &times;
        </button>
      </div>

      <div className="flex-1 flex items-center justify-center relative overflow-hidden">
        <img
          src={photo.url}
          alt={photo.caption || ""}
          className="max-w-full max-h-full object-contain transition-transform duration-200"
          style={{ transform: `scale(${scale})` }}
          onDoubleClick={() => setScale((s) => (s === 1 ? 2 : 1))}
          loading="eager"
        />

        {photos.length > 1 && (
          <>
            <button
              onClick={goPrev}
              disabled={index === 0}
              className="absolute left-2 top-1/2 -translate-y-1/2 text-white text-4xl opacity-50 hover:opacity-100 disabled:opacity-20"
            >
              &#8249;
            </button>
            <button
              onClick={goNext}
              disabled={index === photos.length - 1}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-white text-4xl opacity-50 hover:opacity-100 disabled:opacity-20"
            >
              &#8250;
            </button>
          </>
        )}
      </div>

      {photo.caption && (
        <p className="text-white text-center text-sm px-4 py-2 bg-black/50">
          {photo.caption}
        </p>
      )}
    </div>
  )
}
